import React, { useMemo } from 'react';
import { Typography } from '@rbx/ui';
import { MemberType, ReferenceMemberParameter } from '@modules/client';
import isTypeOptional from '@modules/utilities/pageBuild/isTypeOptional';
import { useReferenceTypesData } from './ReferenceTypesDataProvider';
import ReferenceMemberType from './ReferenceMemberType';
import ReferenceMemberReturns from './ReferenceMemberReturns';
import SpaceSuffixedColon from './SpaceSuffixedColon';
import useReferenceMemberSignatureStyles from './ReferenceMemberSignature.styles';

export type ReferencePropertySignatureData = {
  name: string;
  type: string; // properties must have a concrete type
  parameters?: null | undefined;
  returns?: null | undefined;
};

export type ReferenceCallableSignatureData = {
  name: string;
  type?: undefined; // callables should not carry a 'type' field
  parameters?: Array<ReferenceMemberParameter> | null | undefined;
  returns?: Array<{ type: string }> | null | undefined;
};

export type ReferenceMemberSignatureData =
  | ReferencePropertySignatureData
  | ReferenceCallableSignatureData;

interface ReferenceMemberSignatureProps {
  member: ReferenceMemberSignatureData;
  memberType?: MemberType;
}

function ReferenceMemberSignature({ member, memberType }: ReferenceMemberSignatureProps) {
  const { getTypeAliasDatum, eventReturnTypeAliasDatum } = useReferenceTypesData();
  const { classes } = useReferenceMemberSignatureStyles();

  // Get type information for properties
  const { typeDatum, typeIsOptional } = useMemo(() => {
    const hasType = 'type' in member && typeof member.type !== 'undefined';
    if (hasType) {
      const typeValue = member.type as string;
      const typeDatumVal = getTypeAliasDatum(typeValue);
      if (!typeDatumVal) throw new Error(`No type data found for ${typeValue} but member has type`);
      return {
        typeDatum: typeDatumVal,
        typeIsOptional: isTypeOptional(typeValue),
      };
    }
    return {};
  }, [member, getTypeAliasDatum]);

  // Property signature: "name : Type"
  if (typeDatum) {
    return (
      <Typography variant='body1' component='div' className={classes.signatureContainer}>
        <Typography component='span' className={classes.memberName}>
          {member.name}
        </Typography>
        <SpaceSuffixedColon engineRevampEnabled />
        <ReferenceMemberType
          parameter={typeDatum}
          isOptional={typeIsOptional}
          engineRevampEnabled
        />
      </Typography>
    );
  }

  const isEvent = memberType === MemberType.Event;
  const params = (Array.isArray(member.parameters) && member.parameters) || [];
  const multiline = params.length > 1;

  // Render return type
  const renderReturnType = () => {
    if (isEvent) {
      return (
        <ReferenceMemberType
          parameter={eventReturnTypeAliasDatum}
          isOptional={false}
          engineRevampEnabled
        />
      );
    }
    if (Array.isArray(member.returns) && member.returns.length > 0) {
      return <ReferenceMemberReturns returns={member.returns} engineRevampEnabled />;
    }
    return null;
  };

  // Render single parameter
  const renderParam = (param: ReferenceMemberParameter, showComma: boolean) => (
    <React.Fragment key={param.name}>
      <Typography component='span' className={classes.memberName}>
        {param.name}
      </Typography>
      <SpaceSuffixedColon engineRevampEnabled />
      <ReferenceMemberType
        parameter={param}
        isOptional={isTypeOptional(param.type)}
        engineRevampEnabled
      />
      {showComma && ', '}
    </React.Fragment>
  );

  const returnType = renderReturnType();

  // Method/Event signature: "name(params) : ReturnType"
  return (
    <Typography variant='body1' component='div' className={classes.signatureContainer}>
      {multiline ? (
        <div className={classes.multilineSignature}>
          <div>
            <Typography component='span' className={classes.memberName}>
              {member.name}
            </Typography>
            <span className={classes.parentheses}>(</span>
          </div>
          <div className={classes.parametersContainer}>
            {params.map((param, i) => renderParam(param, i < params.length - 1))}
            {!returnType && <span className={classes.parentheses}> )</span>}
          </div>
          {returnType && (
            <div>
              <span className={classes.parentheses}>)</span>
              <SpaceSuffixedColon engineRevampEnabled />
              {returnType}
            </div>
          )}
        </div>
      ) : (
        <React.Fragment>
          <Typography component='span' className={classes.memberName}>
            {member.name}
          </Typography>
          <span className={classes.parentheses}>(</span>
          {params.map((param, i) => renderParam(param, i < params.length - 1))}
          <span className={classes.parentheses}>)</span>
          {returnType && (
            <React.Fragment>
              <SpaceSuffixedColon engineRevampEnabled />
              {returnType}
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </Typography>
  );
}

export default ReferenceMemberSignature;
